import { escapeHtml } from '@colplat/ui-web';

/**
 * What a debt between wallets would do, said before it is saved.
 *
 * Asked from use: deciding whether 32,407.42 paid by Maintenance for the car
 * is a loan, a repayment, or partly each needs to know where the two stood
 * just before — in which currency — where paying back turns into lending,
 * and where they will stand after. The debts list answers that only for
 * today and only after saving.
 *
 * Shared by the short form and the full editor so the two say it the same
 * way. Words, not colour (invariant #11); the two buttons it may offer —
 * use the zero point as the amount, or count the debt in a currency that is
 * owed — carry their value in data attributes for the caller to wire.
 */
export interface DebtHelperInput {
  /** The wallet the money comes from (the lender, or the one paying back). */
  fromName: string;
  /** The wallet it goes to. */
  toName: string;
  /** Shorter names for the cells, where the table is narrow; the caption keeps the full ones. */
  fromShort?: string | undefined;
  toShort?: string | undefined;
  /**
   * Which of the two is the wallet the reader is entering this for — the
   * form's own Wallet. Its debts read red and what it is owed green; the
   * words under each figure say the same, so colour is never the only sign
   * (invariant #11). Default `from`.
   */
  ownSide?: 'from' | 'to' | undefined;
  /** `loan` or `grant`. */
  kind: string;
  /** The currency this debt is recorded in. */
  currencyCode: string;
  /** How much is being recorded; undefined while it cannot be told yet. */
  amount: number | undefined;
  /** The most it can be: the leg it is measured on. */
  legSize: number | undefined;
  /** `standingBefore`: what `to` owes `from` per currency; negative the other way. */
  standing: Readonly<Record<string, string>>;
  /** Currencies the debt could be recorded in instead — the entry's own two. */
  choices: readonly string[];
  /**
   * A currency chip for a figure, with an optional class (the red or green).
   * Chips rather than bare numbers: the chip says its currency, so the table
   * needs no currency column — and on a phone-width form that column was the
   * one that pushed "After" off the edge.
   */
  chip: (amount: number, currencyCode: string, className?: string) => string;
}

const EPS = 0.00005;

export function debtHelperHtml(input: DebtHelperInput): string {
  const { fromName, toName, currencyCode: ccy, chip } = input;
  /*
   * A table, not sentences: "less anything Renovation already owed it, which
   * this pays back first" was reported as hard to read, and it is — it asks
   * the reader to do the arithmetic the page has already done. A row per
   * currency, and three columns: where they stood, the amount that brings it
   * to zero, where they stand after. Each figure is a chip that says its own
   * currency, with who owes it under it, so no sign convention and no
   * currency column have to be read.
   */
  const fromCell = escapeHtml(input.fromShort || fromName);
  const toCell = escapeHtml(input.toShort || toName);
  const own = input.ownSide ?? 'from';
  const tone = (debtor: 'from' | 'to'): string => (debtor === own ? 'text-danger' : 'text-success');
  // `side` chips (Zero point, After) drop their flag on a narrow form: the
  // Before chip on the same row still says the currency.
  const owes = (m: number, code: string, side = false): string => {
    const extra = side ? ' wl-chip-side' : '';
    return m > EPS
      ? `${chip(m, code, tone('to') + extra)}<div class="text-body-secondary">${toCell} owes</div>`
      : m < -EPS
        ? `${chip(-m, code, tone('from') + extra)}<div class="text-body-secondary">${fromCell} owes</div>`
        : `${chip(0, code, 'is-muted' + extra)}<div class="text-body-secondary">settled</div>`;
  };
  const button = (attr: string, value: string, label: string): string =>
    `<button type="button" class="btn btn-link btn-sm p-0 align-baseline" ${attr}="${escapeHtml(value)}">${label}</button>`;

  const grant = input.kind === 'grant';
  const amount = input.amount !== undefined && input.amount > 0 ? input.amount : undefined;
  const codes = [ccy, ...Object.keys(input.standing).filter((c) => c !== ccy).sort()];

  const rows = codes.map((code) => {
    const before = Number(input.standing[code] ?? 0);
    // What the money's sender owes the receiver: what this entry can pay back.
    const payable = before < -EPS ? -before : 0;
    const mine = code === ccy;
    let zero = '\u2014';
    let after = owes(before, code, true);
    if (mine && !grant) {
      if (payable > 0) {
        zero = chip(payable, code, 'wl-chip-side');
        if (input.legSize === undefined || payable <= input.legSize + EPS) {
          zero += '<div>' + button('data-debt-settle', payable.toFixed(4), 'use this') + '</div>';
        }
      }
      after = amount === undefined ? '\u2014' : owes(before + amount, code, true);
    } else if (!mine && !grant && payable > 0 && input.choices.includes(code)) {
      // Owed the other way in a currency this entry could be counted in.
      zero = `${chip(payable, code, 'wl-chip-side')}<div>${button('data-debt-ccy', code, `count in ${escapeHtml(code)}`)}</div>`;
    }
    // This entry's currency comes first, in bold, and is said in words to a
    // screen reader — weight alone is not a signal (invariant #11).
    const marker = mine ? '<span class="visually-hidden">This entry\u2019s currency: </span>' : '';
    return `<tr${mine ? ' class="fw-semibold"' : ''}>`
      + `<td>${marker}${owes(before, code)}</td><td>${zero}</td><td>${after}</td></tr>`;
  }).join('');

  const note = grant ? '<div>Handed over for good \u2014 it changes nothing between them.</div>' : '';
  return '<div class="small wl-debt-help">'
    + `<table class="table table-sm mb-1 wl-debt-help__t"><caption class="caption-top py-0">Wallets: between `
    + `<strong>${escapeHtml(fromName)}</strong> and <strong>${escapeHtml(toName)}</strong>`
    + ' \u2014 this entry\u2019s currency first</caption>'
    + '<thead><tr><th scope="col">Before</th>'
    + '<th scope="col" title="How much paid back brings it to zero">Zero point</th>'
    + '<th scope="col">After</th></tr></thead>'
    + `<tbody>${rows}</tbody></table>${note}</div>`;
}

/** One line of the entry, as the account assistant shows it. */
export interface AccountAssistantRow {
  accountName: string;
  /** The leg's own currency: its account's balance, its amount and the result are all in it. */
  currencyCode: string;
  /** `accountBalanceBefore`: the account just before this entry. */
  before: number;
  /** This leg, signed. */
  amount: number;
  /** The wallet the leg's money is earmarked to, for the chip's notch; none on a category. */
  walletShort?: string | undefined;
  walletName?: string | undefined;
}

/**
 * What the entry does to each account it touches: the balance before, this
 * line, the balance after.
 *
 * Asked from use, to sit first among the form's two assistants — accounts,
 * then wallets. A row per leg, each in its own currency: dollars out of the
 * dollar account, forints onto the car. The wallet rides in the notch of the
 * line's own chip, where the register puts it too.
 *
 * A grid, not a table, so it can fold: on a narrow form the account name
 * takes its own row and the three figures sit under it (`@container` in
 * styles.css, on the preview box).
 */
export function accountAssistantHtml(
  rows: readonly AccountAssistantRow[],
  chip: (amount: number, currencyCode: string, notch?: { text: string; says?: string }, className?: string) => string,
): string {
  const head = '<div class="wl-q-acct wl-q-acct--head" aria-hidden="true">'
    + '<span>Account</span><span>Before</span><span>This entry</span><span>After</span></div>';
  const body = rows.map((r) => {
    const notch = r.walletShort ? { text: r.walletShort, says: `wallet ${r.walletName ?? r.walletShort}` } : undefined;
    return '<div class="wl-q-acct" role="group" aria-label="' + escapeHtml(r.accountName) + '">'
      // The currency beside the name, shown only on a narrow form, where all
      // three chips drop their flags so the figures fit (styles.css).
      + `<span class="wl-q-acct__name">${escapeHtml(r.accountName)}`
      + `<span class="wl-q-acct__ccy" aria-hidden="true"> \u00b7 ${escapeHtml(r.currencyCode)}</span></span>`
      // Before and after are "side" chips: on a narrow form they drop the
      // flag, which the line's own chip between them still carries.
      + `<span><span class="visually-hidden">before </span>${chip(r.before, r.currencyCode, undefined, 'wl-chip-side')}</span>`
      + `<span><span class="visually-hidden">this entry </span>${chip(r.amount, r.currencyCode, notch)}</span>`
      + `<span><span class="visually-hidden">after </span>${chip(r.before + r.amount, r.currencyCode, undefined, 'wl-chip-side')}</span>`
      + '</div>';
  }).join('');
  return `<div class="wl-q-accts">${head}${body}</div>`;
}
